import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTimezone } from '@contexts/useTimezone';
import { Organization } from '@models/Organization';
import { RosterService, RosterServiceModel } from '@services/RosterService';
import { Logger } from '@utils/Logger';
import Utils from '@utils/Utils';

const isPresentStatus = (status: RosterServiceModel.RosterStatus): boolean =>
    status === 'PRESENTE' || status === 'CON_DEMORA';

// A QontactPoint is classified by aggregating the time windows + fulfillment of its shifts.
// - inProgress: at least one shift has started and not all shifts have ended yet
// - done: every shift has ended and every shift met its expected/assigned headcount
// - notFulfilled: every shift has ended but at least one fell short of its headcount
// - upcoming: no shift has started yet (not surfaced as its own stat, only in total)
export type QontactPointState = 'inProgress' | 'done' | 'notFulfilled' | 'upcoming';

export interface QontactPointSummary {
    total: number;
    inProgress: number;
    done: number;
    notFulfilled: number;
}

const EMPTY_QP_SUMMARY: QontactPointSummary = {
    total: 0,
    inProgress: 0,
    done: 0,
    notFulfilled: 0,
};

const shiftPresentCount = (shift: RosterServiceModel.QontactPointShiftEntry): number =>
    shift.users.filter(u => isPresentStatus(u.status)).length;

const shiftTotalCount = (shift: RosterServiceModel.QontactPointShiftEntry): number =>
    shift.mode === 'count' ? shift.expected : shift.users.length;

const shiftIsFulfilled = (shift: RosterServiceModel.QontactPointShiftEntry): boolean =>
    shiftPresentCount(shift) >= shiftTotalCount(shift);

const classifyQontactPoint = (
    qontactPoint: RosterServiceModel.QontactPointRosterEntry,
    now: number,
): QontactPointState => {
    const shifts = qontactPoint.shifts;
    if (shifts.length === 0) return 'upcoming';

    const anyStarted = shifts.some(s => now >= s.timestampFrom);
    const allEnded = shifts.every(s => now > s.timestampTo);

    if (!anyStarted) return 'upcoming';
    if (!allEnded) return 'inProgress';
    return shifts.every(shiftIsFulfilled) ? 'done' : 'notFulfilled';
};

export interface QontactPointShiftView extends RosterServiceModel.QontactPointShiftEntry {
    presentCount: number;
    totalCount: number;
}

export interface QontactPointView extends RosterServiceModel.QontactPointRosterEntry {
    state: QontactPointState;
    presentCount: number;
    totalCount: number;
    shifts: QontactPointShiftView[];
}

interface UseQontactPointRosterScreenViewModelProps {
    org: Organization;
    rosterService: RosterService;
}

export const useQontactPointRosterScreenViewModel = ({
    org,
    rosterService,
}: UseQontactPointRosterScreenViewModelProps) => {
    const { timezone } = useTimezone();
    const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
    const [qontactPoints, setQontactPoints] = useState<RosterServiceModel.QontactPointRosterEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [stateFilter, setStateFilter] = useState<QontactPointState | null>(null);
    const [expandedQontactPointIDs, setExpandedQontactPointIDs] = useState<Set<string>>(new Set());
    const [expandedShiftIDs, setExpandedShiftIDs] = useState<Set<string>>(new Set());

    const day = useMemo(
        () => Utils.getDayStringFromTimestamp(selectedDate.getTime(), timezone),
        [selectedDate, timezone],
    );

    const formattedDay = useMemo(
        () =>
            selectedDate.toLocaleDateString('es-AR', {
                timeZone: timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }),
        [selectedDate, timezone],
    );

    // Top stat cards: classify each QontactPoint by its aggregate state.
    const summary = useMemo((): QontactPointSummary => {
        const now = Date.now();
        const result = { ...EMPTY_QP_SUMMARY };
        for (const qontactPoint of qontactPoints) {
            result.total += 1;
            switch (classifyQontactPoint(qontactPoint, now)) {
                case 'inProgress': result.inProgress += 1; break;
                case 'done': result.done += 1; break;
                case 'notFulfilled': result.notFulfilled += 1; break;
            }
        }
        return result;
    }, [qontactPoints]);

    // Apply the QontactPoint state filter + the user-name search. Present/total counts
    // and the QP state are always computed from the full (unfiltered) data.
    const filteredQontactPoints = useMemo((): QontactPointView[] => {
        const now = Date.now();
        const query = searchQuery.trim().toLowerCase();

        const result: QontactPointView[] = [];
        for (const qontactPoint of qontactPoints) {
            const state = classifyQontactPoint(qontactPoint, now);
            if (stateFilter != null && state !== stateFilter) continue;

            if (query) {
                const matchesName = qontactPoint.buildingName.toLowerCase().includes(query)
                    || qontactPoint.qontactPointID.toLowerCase().includes(query);
                if (!matchesName) continue;
            }

            const shiftViews: QontactPointShiftView[] = [];
            let qpPresent = 0;
            let qpTotal = 0;

            for (const shift of qontactPoint.shifts) {
                const presentCount = shiftPresentCount(shift);
                const totalCount = shiftTotalCount(shift);
                qpPresent += presentCount;
                qpTotal += totalCount;
                shiftViews.push({ ...shift, presentCount, totalCount });
            }

            result.push({ ...qontactPoint, state, shifts: shiftViews, presentCount: qpPresent, totalCount: qpTotal });
        }
        return result;
    }, [qontactPoints, searchQuery, stateFilter]);

    const toggleQontactPoint = useCallback((qontactPointID: string) => {
        setExpandedQontactPointIDs(prev => {
            const next = new Set(prev);
            next.has(qontactPointID) ? next.delete(qontactPointID) : next.add(qontactPointID);
            return next;
        });
    }, []);

    const toggleShift = useCallback((shiftInstanceID: string) => {
        setExpandedShiftIDs(prev => {
            const next = new Set(prev);
            next.has(shiftInstanceID) ? next.delete(shiftInstanceID) : next.add(shiftInstanceID);
            return next;
        });
    }, []);

    const isQontactPointExpanded = useCallback(
        (qontactPointID: string) => expandedQontactPointIDs.has(qontactPointID),
        [expandedQontactPointIDs],
    );

    const isShiftExpanded = useCallback(
        (shiftInstanceID: string) => expandedShiftIDs.has(shiftInstanceID),
        [expandedShiftIDs],
    );

    const fetchRoster = useCallback(async () => {
        if (!org.id) return;
        setLoading(true);
        try {
            const response = await rosterService.getRosterByQontactPointForDay(org.id, day);
            setQontactPoints(response.qontactPoints);
            // Start collapsed so the list of QontactPoints is easy to scan.
            setExpandedQontactPointIDs(new Set());
            setExpandedShiftIDs(new Set());
        } catch (error) {
            Logger.error(`[QontactPointRosterScreenViewModel] fetchRoster failed: ${String(error)}`);
            setQontactPoints([]);
        } finally {
            setLoading(false);
        }
    }, [org.id, day, rosterService]);

    useEffect(() => {
        void fetchRoster();
    }, [fetchRoster]);

    return {
        qontactPoints: filteredQontactPoints,
        summary,
        loading,
        day,
        selectedDate,
        setSelectedDate,
        formattedDay,
        timezone,
        searchQuery,
        setSearchQuery,
        stateFilter,
        setStateFilter,
        isQontactPointExpanded,
        toggleQontactPoint,
        isShiftExpanded,
        toggleShift,
        refresh: fetchRoster,
    };
};
